import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';

const API = 'https://api.glowytics.com/api/bq-channel-performance';

const fmtNum = (v) => (v == null ? '—' : Number(v).toLocaleString('tr-TR', { maximumFractionDigits: 0 }));
const fmtTRY = (v) => (v == null ? '—' : `₺${Number(v).toLocaleString('tr-TR', { maximumFractionDigits: 0 })}`);
const fmtRatio = (v) => (v == null || !isFinite(v) ? '—' : Number(v).toLocaleString('tr-TR', { maximumFractionDigits: 2 }));

const TR_MONTHS = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];
// BQ'daki `month` alanı 'YYYYMM' string.
const fmtMonth = (m) => {
  if (!m || m.length !== 6) return m || '—';
  return `${TR_MONTHS[Number(m.slice(4, 6)) - 1]} ${m.slice(0, 4)}`;
};

const DIMENSIONS = [
  { key: 'main_channel', label: 'Ana Kanal' },
  { key: 'sub_channel', label: 'Alt Kanal' },
  { key: 'channel_family', label: 'Kanal Ailesi' },
  { key: 'ad_platform', label: 'Reklam Platformu' },
  { key: 'source_medium', label: 'Source / Medium' },
  { key: 'device_category', label: 'Cihaz' },
];

export default function BqChannelPerformanceTest() {
  const { selectedBrand } = useAuth();
  const brandId = selectedBrand?.id || '';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dimension, setDimension] = useState('main_channel');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');

  const load = useCallback(async () => {
    if (!brandId) return;
    setLoading(true);
    setError('');
    try {
      const qs = new URLSearchParams({ dimension });
      if (startDate) qs.set('startDate', startDate);
      if (endDate) qs.set('endDate', endDate);
      const res = await fetch(`${API}/monthly?${qs}`, {
        credentials: 'include',
        headers: { 'x-brand-id': brandId },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
      setData(json);
    } catch (e) {
      setError(e.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [brandId, dimension, startDate, endDate]);

  useEffect(() => { load(); }, [load]);

  const months = useMemo(() => (data?.monthly || []).map((r) => r.month), [data]);

  // Ay seçimi listedeyse korunur, değilse en son aya döner.
  const activeMonth = useMemo(() => {
    if (selectedMonth && months.includes(selectedMonth)) return selectedMonth;
    return months[months.length - 1] || '';
  }, [selectedMonth, months]);

  const dimRows = useMemo(
    () => (data?.byDimension || []).filter((r) => r.month === activeMonth),
    [data, activeMonth]
  );

  const versionRows = useMemo(
    () => (data?.byVersion || []).filter((r) => r.month === activeMonth),
    [data, activeMonth]
  );

  const totals = useMemo(() => {
    const rows = data?.monthly || [];
    return rows.reduce(
      (a, r) => ({
        sessions: a.sessions + r.sessions,
        transactions: a.transactions + r.transactions,
        revenue: a.revenue + r.revenue,
        cost: a.cost + r.cost,
      }),
      { sessions: 0, transactions: 0, revenue: 0, cost: 0 }
    );
  }, [data]);

  if (!brandId) {
    return <div className="p-8 text-gray-500">Marka seçilmedi.</div>;
  }

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">BQ Kanal Performansı — Test</h1>
        <p className="text-sm text-gray-500 mt-1">
          Canlı BigQuery sorgusu. Hiçbir veri kaydedilmiyor — tablonun ne döndürdüğünü görmek için.
        </p>
        {data?.source && (
          <code className="inline-block mt-2 text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
            {data.source}
          </code>
        )}
      </div>

      {/* Kontroller */}
      <div className="flex flex-wrap items-end gap-3 mb-6 p-4 bg-white border border-gray-200 rounded-lg">
        <div>
          <label className="block text-xs text-gray-600 mb-1">Kırılım</label>
          <select
            value={dimension}
            onChange={(e) => setDimension(e.target.value)}
            className="border border-gray-300 rounded px-3 py-1.5 text-sm"
          >
            {DIMENSIONS.map((d) => (
              <option key={d.key} value={d.key}>{d.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-600 mb-1">Başlangıç</label>
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
            className="border border-gray-300 rounded px-3 py-1.5 text-sm" />
        </div>
        <div>
          <label className="block text-xs text-gray-600 mb-1">Bitiş</label>
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
            className="border border-gray-300 rounded px-3 py-1.5 text-sm" />
        </div>
        <button onClick={load} disabled={loading}
          className="px-4 py-1.5 bg-indigo-600 text-white text-sm rounded hover:bg-indigo-700 disabled:opacity-50">
          {loading ? 'Sorgulanıyor…' : 'Yenile'}
        </button>
        {data?.meta && (
          <div className="ml-auto text-xs text-gray-500">
            {data.meta.minDate} → {data.meta.maxDate} · {fmtNum(data.meta.rowCount)} satır
          </div>
        )}
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded text-sm text-red-700">
          <strong>Hata:</strong> {error}
        </div>
      )}

      {/* Tablonun yapısal uyarısı — harcama ve oturum ayrı satır popülasyonlarında */}
      <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded text-sm text-amber-900">
        <strong>Dikkat:</strong> Bu tabloda <code>ad_cost</code> ile <code>sessions</code>/<code>revenue</code> ayrı
        satırlarda duruyor (<code>analytics_version</code> ayrımı). 2026-03'ten sonra hiçbir satır ikisini birden
        taşımıyor — yani satır bazında ROAS hesaplanamaz, harcama ve analytics ayrı toplanıp kanal/platform üzerinden
        eşleştirilmeli. Aşağıdaki "Sürüm Kırılımı" tablosu bu ayrımı gösteriyor.
      </div>

      {/* Toplamlar */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[
            { label: 'Oturum', value: fmtNum(totals.sessions) },
            { label: 'Transaction', value: fmtNum(totals.transactions) },
            { label: 'Ciro', value: fmtTRY(totals.revenue) },
            { label: 'Harcama', value: fmtTRY(totals.cost) },
          ].map((k) => (
            <div key={k.label} className="p-4 bg-white border border-gray-200 rounded-lg">
              <div className="text-xs text-gray-500">{k.label}</div>
              <div className="text-xl font-bold text-gray-900 mt-1">{k.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* Aylık */}
      {data && (
        <div className="mb-6 bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 font-semibold text-gray-800">Aylık Özet</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  {['Ay', 'Oturum', 'Transaction', 'Ciro', 'Adet', 'Harcama', 'Tıklama', 'ROAS', 'CR'].map((h) => (
                    <th key={h} className="px-4 py-2 text-right first:text-left font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.monthly.map((r) => (
                  <tr key={r.month}
                    onClick={() => setSelectedMonth(r.month)}
                    className={`border-t border-gray-100 cursor-pointer hover:bg-indigo-50 ${
                      r.month === activeMonth ? 'bg-indigo-50' : ''
                    }`}>
                    <td className="px-4 py-2 font-medium text-gray-900">{fmtMonth(r.month)}</td>
                    <td className="px-4 py-2 text-right">{fmtNum(r.sessions)}</td>
                    <td className="px-4 py-2 text-right">{fmtNum(r.transactions)}</td>
                    <td className="px-4 py-2 text-right">{fmtTRY(r.revenue)}</td>
                    <td className="px-4 py-2 text-right">{fmtNum(r.quantity)}</td>
                    <td className="px-4 py-2 text-right">{fmtTRY(r.cost)}</td>
                    <td className="px-4 py-2 text-right">{fmtNum(r.clicks)}</td>
                    <td className="px-4 py-2 text-right">{r.cost > 0 ? fmtRatio(r.revenue / r.cost) : '—'}</td>
                    <td className="px-4 py-2 text-right">
                      {r.sessions > 0 ? `%${((r.transactions / r.sessions) * 100).toFixed(2)}` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Sürüm kırılımı */}
      {versionRows.length > 0 && (
        <div className="mb-6 bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 font-semibold text-gray-800">
            Sürüm Kırılımı — {fmtMonth(activeMonth)}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  {['analytics_version', 'Oturum', 'Transaction', 'Ciro', 'Harcama', 'Tıklama'].map((h) => (
                    <th key={h} className="px-4 py-2 text-right first:text-left font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {versionRows.map((r) => (
                  <tr key={r.version} className="border-t border-gray-100">
                    <td className="px-4 py-2 font-mono text-xs text-gray-700">{r.version}</td>
                    <td className="px-4 py-2 text-right">{fmtNum(r.sessions)}</td>
                    <td className="px-4 py-2 text-right">{fmtNum(r.transactions)}</td>
                    <td className="px-4 py-2 text-right">{fmtTRY(r.revenue)}</td>
                    <td className="px-4 py-2 text-right">{fmtTRY(r.cost)}</td>
                    <td className="px-4 py-2 text-right">{fmtNum(r.clicks)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Kırılım */}
      {dimRows.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 font-semibold text-gray-800">
            {DIMENSIONS.find((d) => d.key === dimension)?.label} — {fmtMonth(activeMonth)}
            <span className="ml-2 text-xs font-normal text-gray-500">(ay seçmek için üstteki tabloya tıkla)</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  {['Kırılım', 'Oturum', 'Transaction', 'Ciro', 'Harcama', 'ROAS', 'Sepete Ekleme', 'Checkout'].map((h) => (
                    <th key={h} className="px-4 py-2 text-right first:text-left font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {dimRows.map((r, i) => (
                  <tr key={`${r.dimension}-${i}`} className="border-t border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-2 font-medium text-gray-900">{r.dimension || '—'}</td>
                    <td className="px-4 py-2 text-right">{fmtNum(r.sessions)}</td>
                    <td className="px-4 py-2 text-right">{fmtNum(r.transactions)}</td>
                    <td className="px-4 py-2 text-right">{fmtTRY(r.revenue)}</td>
                    <td className="px-4 py-2 text-right">{fmtTRY(r.cost)}</td>
                    <td className="px-4 py-2 text-right">{r.cost > 0 ? fmtRatio(r.revenue / r.cost) : '—'}</td>
                    <td className="px-4 py-2 text-right">{fmtNum(r.add_to_cart)}</td>
                    <td className="px-4 py-2 text-right">{fmtNum(r.begin_checkout)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
